/**
 * Categorisation Page Component
 * 
 * Main page for managing categories and tags
 * Tab-based interface within Settings layout
 * 
 * Features:
 * - Tab navigation between Categories and Tags
 * - Integration with Settings layout
 * - WCAG 2.2 AA compliant
 * 
 * @see specs/003-categorisation-feature/spec.md (Part 2: Categorisation Feature)
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FolderTree, Tag } from 'lucide-react';
import { CategoriesList } from './categorisation/components/CategoriesList';
import { TagsList } from './categorisation/components/TagsList';
import { PageHeader } from '../../components/PageHeader';

export function CategorisationPage(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'categories' | 'tags'>(
    location.pathname.includes('/tags') ? 'tags' : 'categories'
  );

  // Redirect to default tab if on base categorisation route
  useEffect(() => {
    if (location.pathname === '/settings/categorisation') {
      navigate('/settings/categorisation/categories', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Update active tab based on URL
  useEffect(() => {
    if (location.pathname.includes('/tags')) {
      setActiveTab('tags');
    } else {
      setActiveTab('categories');
    }
  }, [location.pathname]);

  const handleTabChange = (tab: 'categories' | 'tags') => {
    setActiveTab(tab);
    navigate(`/settings/categorisation/${tab}`);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Categorisation"
        description="Manage your transaction categories and tags"
        helpSlug="settings-categorisation"
        headingLevel="h2"
      />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8" aria-label="Categorisation tabs">
          <button
            onClick={() => handleTabChange('categories')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'categories'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            aria-current={activeTab === 'categories' ? 'page' : undefined}
          >
            <FolderTree className="w-5 h-5" aria-hidden="true" />
            <span>Categories</span>
          </button>
          <button
            onClick={() => handleTabChange('tags')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'tags'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            aria-current={activeTab === 'tags' ? 'page' : undefined}
          >
            <Tag className="w-5 h-5" aria-hidden="true" />
            <span>Tags</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'categories' && <CategoriesList />}
        {activeTab === 'tags' && <TagsList />}
      </div>
    </div>
  );
}

